/**
 * AlertsPage — Sprint 5.9 + ALERTS-NOC-REPAIR-UX-1.
 *
 * Aba "Alertas" no NOC. Eventos que exigem ação.
 *  - Cards: Abertos · Críticos · Hoje · ACK · Desatualizados (abertos sem
 *    re-disparo há +24h — provável condição já resolvida).
 *  - Abas de estado: Abertos · Críticos · Reconhecidos · Fechados · Todos.
 *  - Filtros: janela de tempo (24h/7d/30d) · origem (entity_type).
 *  - Tabela: Data (com tooltip absoluto + badge "desatualizado") · Severidade ·
 *    Origem (PT-BR) · Mensagem (+ badge "teste") · Canal · Status · Ações.
 *  - Ações por alerta: Reconhecer (ACK) · Fechar.
 *  - "Verificar agora" (scan): dispara novos alertas E auto-resolve os que já
 *    não valem (device voltou/sumiu, saúde recuperou, OS concluída).
 *  - "Testar canal": valida um notificador (gera 1 alerta rule=test).
 *
 * Polling de 15s. Read-only no que toca aos Sonoffs (nunca envia comando).
 */
import { useCallback, useEffect, useState } from "react";
import {
  ackAlert,
  listAlerts,
  listNotifiers,
  scanAlerts,
  testAlert,
} from "../lib/api.js";
import { formatDate, formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";
import NavIcon from "../nav/NavIcon.jsx";

const POLL_MS = 15000;

const ENTITY_TYPES = [
  "device", "health", "work_order", "inventory", "admin",
];

// S3.11 — abas de estado (separam Abertos/Críticos/Reconhecidos/Fechados).
const ALERT_TABS = [
  { id: "open", label: "Abertos", q: { status: "open" }, emptyWord: "aberto" },
  { id: "critical", label: "Críticos", q: { severity: "critical" }, emptyWord: "crítico" },
  { id: "ack", label: "Reconhecidos", q: { status: "ack" }, emptyWord: "reconhecido" },
  { id: "closed", label: "Fechados", q: { status: "closed" }, emptyWord: "fechado" },
  { id: "all", label: "Todos", q: {}, emptyWord: "" },
];

const SEV_STYLE = {
  info: "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
  warning: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
  critical: "text-noc-err bg-noc-err/10 border-noc-err/30",
};
const STATUS_STYLE = {
  open: "text-noc-err",
  ack: "text-noc-warn",
  closed: "text-noc-muted",
};

// ALERTS-NOC-REPAIR-UX-1 — janelas de tempo (since_hours) p/ filtrar a lista.
const WINDOW_OPTIONS = [
  { v: "", label: "Todas" },
  { v: "24", label: "Últimas 24h" },
  { v: "168", label: "Últimos 7 dias" },
  { v: "720", label: "Últimos 30 dias" },
];
const STALE_MS = 24 * 3600 * 1000;
// Alerta ABERTO que não re-dispara há > 24h: a condição provavelmente já passou
// e ninguém fechou. Sinalizamos p/ não se confundir com incidente atual.
function isStaleOpen(a) {
  if (a.status !== "open" || !a.last_trigger_at) return false;
  return Date.now() - new Date(a.last_trigger_at).getTime() > STALE_MS;
}

function SevPill({ severity }) {
  const cls = SEV_STYLE[severity] || "text-noc-muted bg-noc-bg/40";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>
      {t("alerts.sev_" + severity) || severity}
    </span>
  );
}

export default function AlertsPage({ onOpenDevice }) {
  const [data, setData] = useState({
    alerts: [], total: 0, open_count: 0, critical_count: 0,
    today_count: 0, ack_count: 0, stale_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState("open");
  const [entityType, setEntityType] = useState("");
  const [windowH, setWindowH] = useState(""); // since_hours; "" = todas
  const [testModal, setTestModal] = useState(false);

  const load = useCallback((signal) => {
    const tabQ = (ALERT_TABS.find((x) => x.id === tab) || {}).q || {};
    const active = { ...tabQ };
    if (entityType) active.entity_type = entityType;
    if (windowH) active.since_hours = windowH;
    listAlerts(active, { signal })
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [tab, entityType, windowH]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const reload = () => {
    const ac = new AbortController();
    load(ac.signal);
  };

  async function doAck(alert, action) {
    try {
      await ackAlert(alert.id, action, "noc-admin");
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  async function doScan() {
    try {
      const res = await scanAlerts();
      const s = res.scanned || {};
      const raised = Object.values(s).reduce((a, b) => a + b, 0);
      const resolved = res.resolved || 0;
      setNotice(
        `${t("alerts.scanDone")} — ${raised} novo(s) · ${resolved} resolvido(s) automaticamente.`,
      );
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  return (
    <div className="alerts-page space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-semibold
                          uppercase tracking-[0.14em] text-noc-accent mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-noc-err" />
            Central de prioridades
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-noc-text">
            {t("alerts.title")}
          </h1>
          <p className="text-sm text-noc-muted mt-2">
            Reconheça incidentes, acompanhe recorrências e encerre condições já resolvidas.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button className="btn-primary h-10 gap-2" onClick={doScan}>
            <NavIcon name="refresh" size={15} />
            {t("alerts.btnScan")}
          </button>
          <button className="btn-ghost h-10" onClick={() => setTestModal(true)}>
            {t("alerts.btnTest")}
          </button>
        </div>
      </header>

      {/* Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <DashCard label={t("alerts.cardOpen")}
                  value={data.open_count} tone="red" />
        <DashCard label={t("alerts.cardCritical")}
                  value={data.critical_count} tone="red" />
        <DashCard label={t("alerts.cardToday")}
                  value={data.today_count} tone="cyan" />
        <DashCard label={t("alerts.cardAck")}
                  value={data.ack_count} tone="amber" />
        <DashCard label="Desatualizados"
                  value={data.stale_count} tone="amber"
                  hint="abertos sem atualização há +24h" />
      </div>

      {/* S3.11 — abas de estado + filtro de origem */}
      <div className="glass-card-pad !p-3 flex gap-3 flex-wrap items-center">
        <nav className="flex gap-1 p-1 rounded-xl bg-noc-bg/35 border border-noc-border">
          {ALERT_TABS.map((tb) => (
            <button
              key={tb.id}
              type="button"
              onClick={() => setTab(tb.id)}
              className={`px-3 py-2 text-xs font-semibold rounded-lg ${
                tab === tb.id
                  ? "bg-noc-accent/10 text-noc-accent"
                  : "text-noc-muted hover:text-noc-text hover:bg-noc-panel2"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </nav>
        <span className="flex-1" />
        {/* Janela de tempo (since_hours) */}
        <div>
          <label className="text-[10px] text-noc-muted block mb-0.5">Janela</label>
          <select
            value={windowH}
            onChange={(e) => setWindowH(e.target.value)}
            className="text-xs bg-noc-bg/60 border border-noc-border
                       rounded px-2 py-1 text-noc-text"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.v || "all"} value={o.v}>{o.label}</option>
            ))}
          </select>
        </div>
        <FilterSelect label={t("alerts.filterEntity")}
          value={entityType}
          onChange={setEntityType}
          options={ENTITY_TYPES} tkey="alerts.entity_" />
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">{err}</div>
      )}
      {notice && (
        <div className="text-xs text-noc-ok bg-noc-ok/10
                        border border-noc-ok/30 rounded p-2">
          {notice}
        </div>
      )}

      {/* Tabela */}
      <div className="overflow-x-auto glass-card !p-0 shadow-[0_16px_36px_rgba(2,8,18,0.14)]">
        <table className="w-full text-xs">
          <thead className="text-[10px] font-semibold uppercase tracking-[0.12em] text-noc-muted bg-noc-panel2/50 border-b border-noc-border">
            <tr>
              <th className="text-left font-semibold py-3 px-3">
                {t("alerts.colDate")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("alerts.colSeverity")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("alerts.colSource")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("alerts.colMessage")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("alerts.colChannel")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("alerts.colStatus")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("alerts.colActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-sm text-noc-muted/70
                italic py-3 px-2">{t("common.loading")}</td></tr>
            )}
            {!loading && data.alerts.length === 0 && (
              <tr><td colSpan={7} className="py-8 px-2 text-center">
                <div className="text-2xl mb-1">✓</div>
                <div className="text-sm text-noc-muted">
                  Nenhum alerta {(ALERT_TABS.find((x) => x.id === tab) || {}).emptyWord || ""} no momento.
                </div>
              </td></tr>
            )}
            {data.alerts.map((a) => (
              <tr key={a.id}
                  className="border-b border-noc-border/40
                             hover:bg-noc-panel2/40 transition-colors">
                <td className="py-3 px-3 text-noc-muted whitespace-nowrap"
                    title={formatDate(a.last_trigger_at)}>
                  {formatRelative(a.last_trigger_at)}
                  {a.trigger_count > 1 && (
                    <span className="text-[10px] text-noc-warn ml-1">
                      ×{a.trigger_count}
                    </span>
                  )}
                  {isStaleOpen(a) && (
                    <span
                      className="text-[9px] px-1 py-0.5 ml-1 rounded border
                                 border-noc-warn/30 text-noc-warn/80 align-middle"
                      title="Aberto sem atualização há mais de 24h — provável condição já resolvida. Use 'Verificar agora' para auto-resolver."
                    >
                      desatualizado
                    </span>
                  )}
                </td>
                <td className="py-3 px-3">
                  <SevPill severity={a.severity} />
                </td>
                <td className="py-3 px-3">
                  <span className="text-noc-text">
                    {t("alerts.entity_" + a.entity_type) || a.entity_type}
                  </span>
                  {a.device_id && (onOpenDevice ? (
                    <button
                      type="button"
                      onClick={() => onOpenDevice(a.device_id)}
                      className="font-mono text-[10px] ml-1 text-noc-accent
                                 hover:text-noc-accent underline decoration-dotted"
                      title="Abrir detalhe do dispositivo"
                    >
                      {a.device_id}
                    </button>
                  ) : (
                    <span className="text-noc-muted/70 font-mono text-[10px] ml-1">
                      {a.device_id}
                    </span>
                  ))}
                </td>
                <td className="py-3 px-3 text-noc-text">
                  <div className="font-medium flex items-center gap-1.5">
                    <span>{a.title}</span>
                    {a.rule === "test" && (
                      <span
                        className="text-[9px] px-1 py-0.5 rounded border
                                   border-noc-muted/30 text-noc-muted"
                        title="Alerta gerado pelo botão Testar — não é um incidente real."
                      >
                        teste
                      </span>
                    )}
                  </div>
                  {a.message && (
                    <div className="text-[10px] text-noc-muted/70">
                      {a.message}
                    </div>
                  )}
                </td>
                <td className="py-3 px-3 text-noc-muted">
                  {a.channel}
                </td>
                <td className={"py-1.5 px-2 "
                  + (STATUS_STYLE[a.status] || "")}>
                  {t("alerts.status_" + a.status) || a.status}
                </td>
                <td className="py-3 px-3">
                  {a.status !== "closed" && (
                    <div className="flex gap-1">
                      {a.status === "open" && (
                        <button
                          className="text-[10px] px-1.5 py-0.5 rounded
                                     border border-noc-warn/30
                                     text-noc-warn hover:bg-noc-warn/10"
                          onClick={() => doAck(a, "ack")}
                          title="Reconhecer: marca como visto/em tratamento (continua aberto até ser fechado)"
                        >
                          {t("alerts.btnAck")}
                        </button>
                      )}
                      <button
                        className="text-[10px] px-1.5 py-0.5 rounded
                                   border border-noc-border
                                   hover:bg-noc-bg/50"
                        onClick={() => doAck(a, "close")}
                        title="Fechar: encerra o alerta (sai da lista de abertos)"
                      >
                        {t("alerts.btnClose")}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-noc-muted/70">
        {t("alerts.showing")}: {data.alerts.length}
      </div>

      {testModal && (
        <TestModal
          onClose={() => setTestModal(false)}
          onDone={(msg) => {
            setTestModal(false);
            setNotice(msg);
            reload();
          }}
        />
      )}
    </div>
  );
}

function DashCard({ label, value, tone, hint }) {
  const toneCls = {
    red: "border-noc-err/30 text-noc-err",
    cyan: "border-noc-accent/30 text-noc-accent",
    amber: "border-noc-warn/30 text-noc-warn",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`rounded-xl border ${toneCls} bg-noc-panel p-4`} title={hint || undefined}>
      <div className="font-mono text-2xl font-semibold text-noc-text">{value}</div>
      <div className="text-[11px] font-medium text-noc-muted mt-1">{label}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, tkey }) {
  return (
    <div>
      <label className="text-[10px] text-noc-muted block mb-0.5">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs bg-noc-bg/60 border border-noc-border
                   rounded px-2 py-1 text-noc-text"
      >
        <option value="">— todos —</option>
        {options.map((o) => (
          <option key={o} value={o}>{t(tkey + o) || o}</option>
        ))}
      </select>
    </div>
  );
}

/** Modal de teste de canal — dispara um alerta de teste. */
function TestModal({ onClose, onDone }) {
  const CHANNELS = ["internal", "telegram", "email", "webhook"];
  const [channel, setChannel] = useState("internal");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [notifiers, setNotifiers] = useState([]);

  useEffect(() => {
    const ac = new AbortController();
    listNotifiers({ signal: ac.signal })
      .then(setNotifiers)
      .catch(() => {});
    return () => ac.abort();
  }, []);

  async function submit() {
    setBusy(true);
    setResult(null);
    try {
      const res = await testAlert({
        channel, severity: "info",
        title: "Alerta de teste",
        message: "Disparo de teste do canal " + channel,
      });
      setResult(res);
      if (res.delivered) {
        onDone(t("alerts.testOk") + ` (${channel})`);
      }
    } catch (e) {
      setResult({ delivered: false, detail: e?.message || String(e) });
    } finally {
      setBusy(false);
    }
  }

  const notifState = notifiers.find((n) => n.channel === channel);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-sm space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">
          {t("alerts.btnTest")}
        </h3>
        <div>
          <label className="text-[11px] text-noc-muted block mb-0.5">
            {t("alerts.colChannel")}
          </label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="w-full text-xs bg-noc-bg/60 border
                       border-noc-border rounded px-2 py-1 text-noc-text"
          >
            {CHANNELS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        {notifState && (
          <div className="text-[10px] text-noc-muted">
            {notifState.enabled
              ? (notifState.configured
                  ? t("alerts.channelReady")
                  : t("alerts.channelNotConfigured"))
              : t("alerts.channelDisabled")}
          </div>
        )}
        {result && (
          <div className={"text-[11px] rounded p-1.5 border "
            + (result.delivered
                ? "text-noc-ok bg-noc-ok/10 border-noc-ok/30"
                : "text-noc-warn bg-noc-warn/10 border-noc-warn/30")}>
            {result.detail}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-accent/30
                       text-noc-accent bg-noc-accent/10 hover:bg-noc-accent/10
                       disabled:opacity-50"
            disabled={busy}
            onClick={submit}
          >
            {busy ? t("common.loading") : t("alerts.btnTest")}
          </button>
        </div>
      </div>
    </div>
  );
}
